import React, {useState, useRef, useEffect} from "react";
import {useTranslation} from "react-i18next";
import ReactDOM from "react-dom";
import "./LanguageSwitcher.scss";

const languages = [
  {code: "de", label: "Deutsch", flag: "🇩🇪"},
  {code: "en", label: "English", flag: "🇬🇧"},
  {code: "es", label: "Español", flag: "🇪🇸"},
  {code: "fr", label: "Français", flag: "🇫🇷"},
  {code: "it", label: "Italiano", flag: "🇮🇹"},
];

export default function LanguageSwitcher() {
  const {i18n} = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({top: 0, right: 0});
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const currentCode = i18n.resolvedLanguage || i18n.language || "en";
  const current =
    languages.find(l => currentCode.startsWith(l.code)) || languages[0];

  const changeLanguage = code => {
    i18n.changeLanguage(code);
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
      });
    }
    setIsOpen(prev => !prev);
  };

  useEffect(() => {
    const handleClickOutside = e => {
      const insideTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      const insideMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!insideTrigger && !insideMenu) {
        setIsOpen(false);
      }
    };
    const handleScroll = () => setIsOpen(false);
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  const menu = isOpen
    ? ReactDOM.createPortal(
        <ul
          ref={menuRef}
          className="lang-dropdown__menu"
          style={{top: menuPos.top, right: menuPos.right}}
          role="listbox"
        >
          {languages.map(lang => (
            <li
              key={lang.code}
              className={`lang-dropdown__item${lang.code === current.code ? " active" : ""}`}
              onMouseDown={e => e.stopPropagation()}
              onClick={() => changeLanguage(lang.code)}
              role="option"
              aria-selected={lang.code === current.code}
            >
              <span className="lang-dropdown__item-flag">{lang.flag}</span>
              <span className="lang-dropdown__item-label">{lang.label}</span>
            </li>
          ))}
        </ul>,
        document.body
      )
    : null;

  return (
    <div className="lang-dropdown">
      <button
        ref={triggerRef}
        className="lang-dropdown__trigger"
        onClick={handleToggle}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="lang-dropdown__flag">{current.flag}</span>
        <span className="lang-dropdown__code">{current.code.toUpperCase()}</span>
        <span className={`lang-dropdown__chevron${isOpen ? " open" : ""}`}>▾</span>
      </button>
      {menu}
    </div>
  );
}
